import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { isApiError } from "../../api/client.js";
import { createOrganization } from "../../api/organizations-api.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { AuthAlert } from "../auth/entry/AuthAlert.js";
import {
  AUTH_ENTRY_FIELD_ERROR,
  AUTH_ENTRY_FIELD_LABEL,
  AUTH_ENTRY_INPUT,
  AUTH_ENTRY_PAIRED_ACTION,
  AUTH_ENTRY_PRIMARY_ACTION,
} from "../auth/entry/auth-entry-styles.js";

interface CreateOrganizationFormProps {
  onCreated: (organizationId: number) => void;
  submitLabel?: string;
  submittingLabel?: string;
  /** Shown beside submit at equal size; style it with `AUTH_ENTRY_PAIRED_ACTION`. */
  secondaryAction?: ReactNode;
  autoFocus?: boolean;
  className?: string;
}

const FIELD_ERROR_ID = "org-name-error";
const SERVER_ERROR_ID = "org-name-server-error";

export function CreateOrganizationForm({
  onCreated,
  submitLabel,
  submittingLabel,
  secondaryAction,
  autoFocus = false,
  className,
}: CreateOrganizationFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // State updates are async; the ref blocks a second submit before re-render.
  const submitLock = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const trimmed = name.trim();
  const validationError = trimmed.length === 0 ? t("organization.create.nameRequired") : null;
  const showValidation = touched && validationError !== null;
  const describedBy =
    [showValidation ? FIELD_ERROR_ID : null, error ? SERVER_ERROR_ID : null].filter(Boolean).join(" ") ||
    undefined;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (validationError || submitLock.current) return;

    submitLock.current = true;
    setSubmitting(true);
    setError(null);
    setErrorCode(null);
    try {
      const created = await createOrganization(trimmed);
      onCreated(created.id);
    } catch (e) {
      if (isApiError(e)) {
        setError(e.message);
        setErrorCode(e.code ?? null);
      } else {
        setError(t("organization.create.backendError"));
      }
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={cn("@container grid min-w-0 gap-5", className)} noValidate>
      <div className="grid gap-2">
        <label className={AUTH_ENTRY_FIELD_LABEL} htmlFor="org-name">
          {t("organization.create.nameLabel")}
        </label>
        <div className="relative">
          <input
            id="org-name"
            type="text"
            className={cn(AUTH_ENTRY_INPUT, showValidation && "pe-10")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            maxLength={200}
            autoComplete="organization"
            autoFocus={autoFocus}
            disabled={submitting}
            placeholder={t("organization.create.namePlaceholder")}
            aria-invalid={showValidation}
            aria-describedby={describedBy}
          />
          {showValidation ? (
            <AlertCircle
              size={18}
              className="pointer-events-none absolute end-3.5 top-1/2 -translate-y-1/2 text-[#e06a5f]"
              aria-hidden="true"
            />
          ) : null}
        </div>
        {showValidation ? (
          <p id={FIELD_ERROR_ID} className={AUTH_ENTRY_FIELD_ERROR}>
            {validationError}
          </p>
        ) : null}
      </div>

      {error ? (
        <div id={SERVER_ERROR_ID}>
          <AuthAlert>
            {error}
            {errorCode ? (
              <span className="mt-1 block font-mono text-[12px] font-[500] text-[#8f3a33]">
                {t("organization.create.code", { code: errorCode })}
              </span>
            ) : null}
          </AuthAlert>
        </div>
      ) : null}

      <div
        className={cn("grid gap-2.5", secondaryAction ? "@min-[20.5rem]:grid-cols-2" : undefined)}
        data-testid="create-organization-actions"
      >
        <Button
          type="submit"
          className={cn(AUTH_ENTRY_PRIMARY_ACTION, secondaryAction ? AUTH_ENTRY_PAIRED_ACTION : undefined)}
          disabled={submitting || trimmed.length === 0}
        >
          {submitting
            ? (submittingLabel ?? t("organization.create.submitting"))
            : (submitLabel ?? t("organization.create.submit"))}
        </Button>
        {secondaryAction}
      </div>
    </form>
  );
}
